/**
 * Piece library.
 *
 * Every piece has one fixed orientation. There is no rotation control, and
 * nothing re-orients a piece on the way down: what the tray shows is exactly
 * what lands on the face.
 *
 * A piece is a set of paths walked out from an anchor cell, each path a
 * sequence of axis labels followed by geometry.js `step`. Two rules keep the
 * orientation honest:
 *
 *   - The anchor must have the parity the piece was defined with. Upward cells
 *     step by decrementing a coordinate and downward cells by incrementing, so
 *     dropping the same paths on the wrong parity would come out rotated. With
 *     the parity pinned, every legal placement is a pure translation of the
 *     shape drawn in the tray.
 *   - All of a piece's cells must land on one face. Walking off a face edge is
 *     no placement at all.
 *
 * Because parity is pinned, orientations that a rotation control would have
 * produced are separate entries instead: the two single triangles and the three
 * rhombi.
 *
 * Sizes run 1 to 3. There are no 4-cell pieces: the size-2 triangle would drop
 * a whole 4x Pyramid formation in a single move.
 *
 * The 3-cell polyiamond is the straight 3-in-a-row, and all six of its
 * 60-degree orientations are here: one per axis, in each parity. Two of them
 * (`bar-*-up`) can land exactly on a face's 3-cell bar and clear it outright —
 * that is the cheapest formation in the game, so it reads as a small reward
 * rather than a free win.
 */

import { AXIS_A, AXIS_B, AXIS_C, CELLS, step } from './geometry.js';

/**
 * @type {{key: string, name: string, up: boolean, paths: number[][], size: number}[]}
 */
export const PIECES = [
  {
    key: 'up',
    name: 'Moniamond',
    up: true,
    paths: [[]],
  },
  {
    key: 'down',
    name: 'Inverted Moniamond',
    up: false,
    paths: [[]],
  },
  {
    key: 'rhombus-flat',
    name: 'Diamond',
    up: true,
    paths: [[], [AXIS_C]],
  },
  {
    key: 'rhombus-right',
    name: 'Right Diamond',
    up: true,
    paths: [[], [AXIS_A]],
  },
  {
    key: 'rhombus-left',
    name: 'Left Diamond',
    up: true,
    paths: [[], [AXIS_B]],
  },

  // The six triamonds. A straight run along an axis alternates steps in the
  // other two, so the axis a run travels along is the one it never steps
  // across: `bar-c-*` runs level with the base, `bar-b-*` and `bar-a-*` run
  // parallel to a sloped edge.
  {
    key: 'bar-c-up',
    name: 'Level Bar',
    up: true,
    paths: [[], [AXIS_A], [AXIS_A, AXIS_B]],
  },
  {
    key: 'bar-c-down',
    name: 'Level Bar (inverted)',
    up: false,
    paths: [[], [AXIS_A], [AXIS_A, AXIS_B]],
  },
  {
    key: 'bar-b-up',
    name: 'Right Bar',
    up: true,
    paths: [[], [AXIS_A], [AXIS_A, AXIS_C]],
  },
  {
    key: 'bar-b-down',
    name: 'Right Bar (inverted)',
    up: false,
    paths: [[], [AXIS_A], [AXIS_A, AXIS_C]],
  },
  {
    key: 'bar-a-up',
    name: 'Left Bar',
    up: true,
    paths: [[], [AXIS_B], [AXIS_B, AXIS_C]],
  },
  {
    key: 'bar-a-down',
    name: 'Left Bar (inverted)',
    up: false,
    paths: [[], [AXIS_B], [AXIS_B, AXIS_C]],
  },
].map((piece) => ({ ...piece, size: piece.paths.length }));

export const PIECES_BY_KEY = new Map(PIECES.map((p) => [p.key, p]));

/**
 * Map a piece onto the surface with its first cell on `anchorId`.
 *
 * @returns {number[] | null} cell ids, or null if the placement is not legal:
 *          the anchor has the wrong parity, a path runs off the face, or two
 *          paths collapse onto the same cell.
 */
export function realizeOnSurface(anchorId, piece) {
  const anchor = CELLS[anchorId];
  if (anchor.up !== piece.up) return null; // would land rotated

  const out = [];

  for (const path of piece.paths) {
    let id = anchorId;
    for (const axis of path) {
      id = step(id, axis);
      if (id === null) return null; // walked off the face
    }
    if (out.includes(id)) return null;
    out.push(id);
  }

  return out;
}

/* ---------------------------------------------------------------------------
 * Tray drawing
 * ------------------------------------------------------------------------- */

const HEX_H = Math.sqrt(3) / 2;

/**
 * Walk a piece in an unbounded triangular lattice rather than on the pyramid,
 * to draw it in the tray. Coordinates are integer triples; upward cells
 * decrement and downward cells increment, matching `step`.
 */
function realizeAbstract(piece) {
  return piece.paths.map((path) => {
    const at = [0, 0, 0];
    let up = piece.up;
    for (const axis of path) {
      at[axis] += up ? -1 : 1;
      up = !up;
    }
    return { coords: at, up };
  });
}

/**
 * 2D polygons for drawing a piece in the tray, in lattice units. The same
 * paths and parity the board uses, so the tray is a faithful preview.
 *
 * @returns {{points: number[][], up: boolean}[]}
 */
export function piecePolygons(piece) {
  const vertex = (i, j) => [j - i / 2, i * HEX_H];

  return realizeAbstract(piece).map(({ coords, up }) => {
    const row = -coords[2];
    const idx = coords[1];
    const points = up
      ? [vertex(row, idx), vertex(row + 1, idx), vertex(row + 1, idx + 1)]
      : [vertex(row, idx), vertex(row, idx + 1), vertex(row + 1, idx + 1)];
    return { points, up };
  });
}
